/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 7b: the workouts of one program for the planner. Every change waits
 * for the server and then replaces state with new objects, never mutating a card.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteWorkoutFromProgramming,
  loadAllWorkoutCard,
  loadWorkoutCard_DateRange,
  newWorkoutCards,
  updateWorkoutCard_Backend,
} from "@lemonadetraining/web-shared";
import type { WorkoutData } from "@lemonadetraining/contracts";

/** WorkoutData as the planner holds it: dates as Date (the callables send them as ISO strings). */
export type PlanData = Omit<WorkoutData, "date"> & { date?: Date | null };

export interface PlannedWorkout {
  id: string;
  index: number;
  programmingUID: string;
  workoutData: PlanData;
}

export type DateWindow = { from: Date; to: Date };
export type WorkoutRange = { kind: "dates"; windows: DateWindow[] } | { kind: "all" };

type Status = "loading" | "ready" | "error";

const toDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const maybe = value as { toDate?: () => Date; seconds?: number };
  if (typeof maybe.toDate === "function") return maybe.toDate();
  if (typeof maybe.seconds === "number") return new Date(maybe.seconds * 1000);
  const parsed = new Date(value as string);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const fromDoc = (id: string, raw: any, programmingUID: string): PlannedWorkout => {
  const { date, ...data } = raw?.workoutData ?? {};
  const videos: string[] = Array.isArray(data.youTubeIDs)
    ? data.youTubeIDs.filter((v: unknown): v is string => typeof v === "string" && v !== "")
    : [];
  // The old single-video field still plays on the phone app; show it with the rest.
  if (typeof data.youTubeID === "string" && data.youTubeID && !videos.includes(data.youTubeID)) {
    videos.unshift(data.youTubeID);
  }
  const when = toDate(date);
  return {
    id,
    index: typeof raw?.index === "number" ? raw.index : 0,
    programmingUID,
    workoutData: {
      ...data,
      title: data.title ?? "",
      description: data.description ?? "",
      tags: Array.isArray(data.tags) ? [...data.tags] : [],
      youTubeIDs: videos,
      movements: Array.isArray(data.movements) ? data.movements.map((m: any) => ({ ...m })) : [],
      ...(when ? { date: when } : {}),
    },
  };
};

/*
 * What goes to the callables. The SDK sends `undefined` as null, so absent stays
 * absent; a missing date is not written as null; the legacy single video now
 * lives in youTubeIDs (the whole map is replaced, so dropping it clears it).
 */
const toStored = (data: PlanData): PlanData => {
  const { youTubeID: _legacyVideo, ...rest } = data;
  return Object.fromEntries(
    Object.entries(rest).filter(([key, v]) => v !== undefined && !(key === "date" && v === null))
  ) as PlanData;
};

export const byIndex = (a: PlannedWorkout, b: PlannedWorkout) => a.index - b.index;

const rangeKey = (range: WorkoutRange) =>
  range.kind === "all"
    ? "all"
    : range.windows.map((w) => `${w.from.getTime()}-${w.to.getTime()}`).join("|");

export const useProgramWorkouts = (programmingUID: string, range: WorkoutRange) => {
  const [workouts, setWorkouts] = useState<PlannedWorkout[]>([]);
  const [status, setStatus] = useState<Status>("loading");
  const [attempt, setAttempt] = useState(0);
  const key = rangeKey(range);
  const rangeRef = useRef(range);
  rangeRef.current = range;
  const latest = useRef<PlannedWorkout[]>([]);
  latest.current = workouts;
  const reserved = useRef(-1);

  useEffect(() => {
    let live = true;
    setStatus("loading");
    const current = rangeRef.current;
    const reads =
      current.kind === "all"
        ? [loadAllWorkoutCard(programmingUID)]
        : current.windows.map((w) => loadWorkoutCard_DateRange(programmingUID, w.from, w.to));
    Promise.all(reads)
      .then((snapshots: any[]) => {
        if (!live) return;
        const byId = new Map<string, PlannedWorkout>();
        snapshots.forEach((snapshot) =>
          snapshot.forEach((doc: any) =>
            byId.set(doc.id, fromDoc(doc.id, doc.data(), programmingUID))
          )
        );
        setWorkouts([...byId.values()].sort(byIndex));
        setStatus("ready");
      })
      .catch((error: unknown) => {
        console.error("Error loading the program's workouts:", error);
        if (live) setStatus("error");
      });
    return () => {
      live = false;
    };
  }, [programmingUID, key, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  /** Resolves with the new workout once the server has it; rejects without touching the list. */
  const create = useCallback(
    async (data: PlanData, targetProgrammingUID = programmingUID, index?: number) => {
      // Reserved before the await, so two quick adds never share a place in the order.
      const workoutIndex =
        index ??
        (reserved.current =
          Math.max(
            reserved.current,
            latest.current.reduce((max, w) => Math.max(max, w.index), -1)
          ) + 1);
      const stored = toStored(data);
      const ret: any = await newWorkoutCards({
        programmingUID: targetProgrammingUID,
        workoutData: stored,
        index: workoutIndex,
      });
      const created = fromDoc(
        ret.data,
        { workoutData: stored, index: workoutIndex },
        targetProgrammingUID
      );
      if (targetProgrammingUID === programmingUID) {
        latest.current = [...latest.current, created].sort(byIndex);
        setWorkouts(latest.current);
      }
      return created;
    },
    [programmingUID]
  );

  const update = useCallback(
    async (workout: PlannedWorkout, data: PlanData) => {
      const stored = toStored(data);
      await updateWorkoutCard_Backend({ UID: workout.id, programmingUID, workoutData: stored });
      const saved = fromDoc(
        workout.id,
        { workoutData: stored, index: workout.index },
        programmingUID
      );
      setWorkouts((prev) => prev.map((w) => (w.id === workout.id ? saved : w)));
      return saved;
    },
    [programmingUID]
  );

  const remove = useCallback(
    async (workout: PlannedWorkout) => {
      await deleteWorkoutFromProgramming({ UID: workout.id, programmingUID });
      setWorkouts((prev) => prev.filter((w) => w.id !== workout.id));
    },
    [programmingUID]
  );

  return { workouts, status, reload, create, update, remove };
};
